import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { EventsCollectionQueryModel } from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { EventsFilterGroup } from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterGroup';
import { EventsFilterGroupComputeds } from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterGroupComputeds';
import { EventsList } from 'src/domains/sportsbook/webview/components/eventsList/EventsList';
import { FilterKindType } from 'src/domains/sportsbook/webview/components/eventsFilters/EventsFilterType';
import { FiltersSkinType } from 'src/domains/layouts/webview/components/filters/FiltersSkin';
import { PriceChangeLegend } from 'src/domains/layouts/webview/components/priceChangeLegend/PriceChangeLegend';
import { SportHeaderStar } from 'src/domains/layouts/webview/components/sportHeader/SportHeader';
import { SpecialSportsState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportsState';
import NFLlogo from 'src/domains/sportsbook/webview/components/eventsList/eventRowNFL/assets/Official_NFL_logo.webp';
import {
    EventsNFLLogoImage,
    EventsNFLLogoImageWrapper,
    EventsTableHeader,
    FiltersStyle,
    Heading,
    HeadingTitle,
} from 'src/domains/sportsbook/webview/components/eventsTable/EventsTable.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Collapse } from 'src/ui/components/collapse/sports/01/Collapse';
import { SpecialSportsListState } from 'src/domains/sportsbook/state/specialSportsState/SpecialSportListState';
import { EventMainSection } from 'src/domains/sportsbook/webview/components/eventsTable/EventsMainSectionWrapper.style';

interface PropsType {
    showHeaderSport: boolean;
    allGroupsOpen: boolean;
    title: React.ReactNode;
    icon: 'in-play' | 'star-fill' | null;
    isNotOutright?: boolean;
    filters: Array<FilterKindType>;
    headerStyle?: EventsTableHeaderStyle;
    isTogglable: boolean;
    showPriceLegend: boolean;
    dataTest?: string;
    eventsFilter: EventsFilterGroupComputeds;
    collection: EventsCollectionQueryModel;
    filterVersion: FiltersSkinType;
    isCompetitionPage?: boolean;
    isAmericanFootball: boolean;
    specialSportState: SpecialSportsState | null;
}

export enum EventsTableHeaderStyle {
    SOLID = 'solid',
    DOUBLE = 'double',
}

export const EventsMainSectionWrapper = observer('EventsMainSectionWrapper', (props: PropsType): JSX.Element => {
    const {
        allGroupsOpen,
        dataTest,
        eventsFilter,
        headerStyle = EventsTableHeaderStyle.SOLID,
        icon,
        isNotOutright = true,
        filters,
        isTogglable,
        showHeaderSport,
        showPriceLegend,
        title,
        collection,
        filterVersion,
        isCompetitionPage,
        isAmericanFootball,
        specialSportState,
    } = props;
    const common = useCommon();
    const configState = ConfigComponents.get(common).unifiedComponentConfig;
    const { sportsbookVariant } = configState.componentVariants;

    const isNotSpecial = specialSportState === null || specialSportState.isSpecial === false;
    const isSpecialNotLoading = specialSportState === null || SpecialSportsListState.get(common).isLoading === false;

    return (
        <EventMainSection
            data-test={dataTest}
            unified={configState.unifiedSportEventsTable}
        >
            {isNotSpecial ? (
                isTogglable ? (
                    <Collapse
                        dataTest='collapse-main-section'
                        handleToggle={eventsFilter.flag.onToggle}
                        isOpen={eventsFilter.flag.isOpen}
                        title={title}
                    />
                ) : (
                    <EventsTableHeader
                        headerStyle={headerStyle}
                        variant={sportsbookVariant}
                    >
                        <Heading
                            headerStyle={headerStyle}
                            variant={sportsbookVariant}
                        >
                            <HeadingTitle>{title}</HeadingTitle>
                        </Heading>
                    </EventsTableHeader>
                )
            ) : null}

            {eventsFilter.flag.isOpen ? (
                <>
                    {isNotOutright && isNotSpecial && isSpecialNotLoading && filters.length > 0 ? (
                        <FiltersStyle
                            headerStyle={headerStyle}
                            isCompetitionPage={isCompetitionPage}
                            variant={sportsbookVariant}
                        >
                            <EventsFilterGroup
                                skin={filterVersion}
                                eventsFilter={eventsFilter}
                                filters={filters}
                                key='header-filters'
                            />
                        </FiltersStyle>
                    ) : null}

                    {eventsFilter.isSpecialSport ? (
                        <SportHeaderStar
                            options={{ desktopImage: '', mobileImage: '', tabletImage: '' }}
                            isSpecialEvent={true}
                            sportId={specialSportState?.sportId ?? ''}
                            isCompetitionPage={false}
                            competitionId={null}
                        />
                    ) : null}

                    <EventsList
                        events={collection}
                        filterMarketsByPopularTag={icon === 'in-play' || icon === 'star-fill'}
                        showHeaderSport={showHeaderSport}
                        allGroupsOpen={allGroupsOpen}
                        specialSportsState={specialSportState}
                    />

                    {showPriceLegend ? <PriceChangeLegend isRace={false} /> : null}

                    {isAmericanFootball ? (
                        <EventsNFLLogoImageWrapper>
                            <EventsNFLLogoImage
                                src={NFLlogo}
                                alt='Official NFL logo'
                            />
                        </EventsNFLLogoImageWrapper>
                    ) : null}
                </>
            ) : null}
        </EventMainSection>
    );
});
